import type { RecordSchema, SelectOptionItem } from '@/types'
import useEmployeesApi from '~/services/apis/employeesApi'

export const useStaffList = (props?: { purchaseId: MaybeRefOrGetter<string> }) => {
	const { getEmployeeList } = useEmployeesApi()
	const purcshaseId = toRef(props?.purchaseId ?? '')
	const staffList = useState<{
		list: (SelectOptionItem & EmployeesResponse)[]
		purchaseId: string
	}>('employee-list', () => ({
		list: [],
		purchaseId: purcshaseId.value
	}))

	// Staff Query
	// const staffList = ref<SelectOptionContext>([])
	const paginationFilter = reactive({
		total_count: 0,
		total_pages: 1,
		page: 1,
		current_page: 1,
		loading: false
	})
	const fetchStaff = async (arg?: RecordSchema) => {
		const setList = (list: EmployeesResponse[]) => {
			return list.map(item => ({
				...item,
				value: item.id,
				label: `${item.first_name} ${item.last_name}`
			}))
		}
		try {
			paginationFilter.loading = paginationFilter.current_page > 1
			const result = await getEmployeeList({
				purchase_id: purcshaseId.value,
				page: paginationFilter.current_page,
				...(arg || {})
			})
			const response = result?.data ?? null
			if (response) {
				paginationFilter.total_count = Number(response.total_count ?? 0)
				paginationFilter.total_pages = Number(response.total_pages ?? 0)
				paginationFilter.current_page++
				const list = setList(response.items ?? [])

				// Filter Out to make sure no duplicates is found
				if (!staffList.value.list.length) {
					staffList.value.list = list.map(item => ({
						...item,
						value: item.id,
						label: `${item.first_name} ${item.last_name}`
					}))
				} else {
					list.forEach(item => {
						if (item.id) {
							if (staffList.value.list.every(existingItem => existingItem.id !== item.id)) {
								staffList.value.list.push({
									...item,
									value: item.id,
									label: `${item.first_name} ${item.last_name}`
								})
							}
						}
					})
				}
				return list
			}

			return []
		} finally {
			paginationFilter.loading = false
		}
	}
	const extractStaffUsingId = <T = EmployeesResponse>(id: string, list = staffList.value.list) => {
		return (list.find(item => item.id === id) ?? null) as T
	}

	const scrollingItem = async (e: Event) => {
		const targetEl = e.target as HTMLElement
		if (targetEl) {
			const scrollTop = targetEl.scrollTop
			const clientHeight = targetEl.clientHeight
			const scrollHeight = targetEl.scrollHeight
			// Check if we have scrolled to the end
			const isAtTheEnd = scrollTop + clientHeight >= scrollHeight
			if (isAtTheEnd && paginationFilter.current_page < paginationFilter.total_pages + 1) {
				await fetchStaff()
				targetEl.scroll({ behavior: 'smooth', top: scrollHeight - paginationFilter.current_page })
			}
		}
	}
	const attachStaffInfo = (e: EmployeesResponse | null, staffRecord: Record<string, DynamicTableRecord>) => {
		if (e && staffRecord) {
			staffRecord.first_name = {
				...(staffRecord?.first_name ?? {}),
				value: e.first_name
			}
			staffRecord.last_name = {
				...(staffRecord?.last_name ?? {}),
				value: e.last_name
			}
			staffRecord.email = {
				...(staffRecord?.email ?? {}),
				value: e.email
			}
			staffRecord.phone_number = {
				...(staffRecord?.phone_number ?? {}),
				value: e.phone
			}
		}
	}

	return {
		extractStaffUsingId,
		scrollingItem,
		paginationFilter,
		fetchStaff,
		attachStaffInfo,
		staffList: computed(() => staffList.value.list)
	}
}
